import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const loginTimeRef = useRef(null);

  // Set axios default header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      loadUser();
      // Record login time for session tracking
      loginTimeRef.current = Date.now();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Track session time on page unload
  useEffect(() => {
    const handleBeforeUnload = async () => {
      await sendSessionTime();
    };

    // Also track on visibility change (when user switches tabs or minimizes)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && token && loginTimeRef.current) {
        sendSessionTimeSync();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token]);

  // Synchronous send for beforeunload (best effort)
  const sendSessionTimeSync = () => {
    if (!token || !loginTimeRef.current) return;
    
    const timeSpent = Math.floor((Date.now() - loginTimeRef.current) / 1000);
    if (timeSpent <= 0) return;
    
    // Reset login time after sending to avoid double counting
    loginTimeRef.current = Date.now();
    
    try {
      // Use fetch with keepalive for reliable delivery on page unload
      fetch(`${API_URL}/api/auth/update-session-time`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ time_spent: timeSpent }),
        keepalive: true
      });
    } catch (error) {
      console.error('Failed to send session time:', error);
    }
  };

  // Send session time to backend (async version)
  const sendSessionTime = async () => {
    if (!token || !loginTimeRef.current) return;
    
    const timeSpent = Math.floor((Date.now() - loginTimeRef.current) / 1000);
    
    if (timeSpent <= 0) return;
    
    // Reset login time after sending to avoid double counting
    loginTimeRef.current = Date.now();
    
    try {
      await axios.post(`${API_URL}/api/auth/update-session-time`, {
        time_spent: timeSpent
      });
    } catch (error) {
      console.error('Failed to send session time:', error);
    }
  };

  const loadUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/profile`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to load user:', error);
      // Check if this might be an admin token - don't logout if token exists
      // The admin dashboard will verify the token separately
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        // Try to decode the token to check if it's an admin token
        try {
          const payload = JSON.parse(atob(storedToken.split('.')[1]));
          if (payload.type === 'admin') {
            // It's an admin token - set a basic admin user
            setUser({
              id: payload.sub,
              name: 'Admin',
              email: 'admin@knowmyschemes.com',
              is_admin: true
            });
            setLoading(false);
            return;
          }
        } catch (e) {
          console.log('Token decode failed');
        }
      }
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (phone, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        phone,
        password
      });
      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(user);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      loginTimeRef.current = Date.now();
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  // OTP-based login: verify OTP, receive JWT
  const loginWithOTP = async (phone, otp) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-otp`, { phone, otp });
      const data = response.data;
      if (data.success && data.user_exists && data.access_token) {
        localStorage.setItem('token', data.access_token);
        setToken(data.access_token);
        setUser(data.user);
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
        loginTimeRef.current = Date.now();
        return { success: true };
      }
      // Phone verified but no account
      return { success: false, verified: true, user_exists: false, error: data.message || 'Please sign up first' };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'OTP verification failed' };
    }
  };

  // OTP-based signup: verify OTP + create account
  const signupWithOTP = async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/signup-with-otp`, userData);
      const data = response.data;
      if (data.success && data.access_token) {
        localStorage.setItem('token', data.access_token);
        setToken(data.access_token);
        setUser(data.user);
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
        loginTimeRef.current = Date.now();
        return { success: true, token: data.access_token };
      }
      return { success: false, error: 'Signup failed' };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Signup failed' };
    }
  };

  const signup = async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/signup`, userData);
      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(user);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      loginTimeRef.current = Date.now();
      return { success: true, token: access_token };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Signup failed' 
      };
    }
  };

  const logout = async () => {
    // Send final session time before logout
    if (token && loginTimeRef.current) {
      const timeSpent = Math.floor((Date.now() - loginTimeRef.current) / 1000);
      if (timeSpent > 0) {
        try {
          await axios.post(`${API_URL}/api/auth/update-session-time`, {
            time_spent: timeSpent
          });
        } catch (error) {
          console.error('Failed to send session time on logout:', error);
        }
      }
    }
    
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    loginTimeRef.current = null;
    delete axios.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    token,
    loading,
    login,
    loginWithOTP,
    signup,
    signupWithOTP,
    logout,
    setUser,
    setToken,
    refreshUser: loadUser,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin === true
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
